import CATALOG from "./geolibreCatalog.json";

const PRESENTATION = CATALOG.layers;

const LULC_SOURCE_WORKSPACE = "LULC_level_3";

export const GEOLIBRE_LULC_YEARS = [
  { label: "2017-2018", value: "17_18" },
  { label: "2018-2019", value: "18_19" },
  { label: "2019-2020", value: "19_20" },
  { label: "2020-2021", value: "20_21" },
  { label: "2021-2022", value: "21_22" },
  { label: "2022-2023", value: "22_23" },
  { label: "2023-2024", value: "23_24" },
  { label: "2024-2025", value: "24_25" },
];

export const LATEST_GEOLIBRE_LULC_YEAR =
  GEOLIBRE_LULC_YEARS[GEOLIBRE_LULC_YEARS.length - 1].value;

// Colors match the CoRE Stack workToColorMapping. A category with no entry
// in that mapping (irrigation_non_rwh) uses its Default fallback color.
export const GEOLIBRE_NREGA_CATEGORIES = Object.freeze([
  {
    id: "land_restoration",
    label: "Land restoration",
    color: "#FFA500",
    values: ["Agri Impact - HH,  Community"],
  },
  {
    id: "livelihood",
    label: "Household livelihood",
    color: "#C2678D",
    values: ["Household Livelihood"],
  },
  {
    id: "irrigation_site",
    label: "Irrigation — site-level impact",
    color: "#1A759F",
    values: ["Irrigation - Site level impact"],
  },
  {
    id: "community",
    label: "Community assets",
    color: "#355070",
    values: ["Others - HH, Community"],
  },
  {
    id: "plantation",
    label: "Plantation and forestry",
    color: "#52B69A",
    values: ["Plantation"],
  },
  {
    id: "soil_water_conservation",
    label: "Soil and water conservation",
    color: "#6495ED",
    values: ["SWC - Landscape level impact"],
  },
  {
    id: "unclassified",
    label: "Other or unclassified",
    color: "#6D597A",
    values: ["", "Un Identified"],
    fallback: true,
  },
]);

const LAYERS = [
  {
    id: "administrative_boundaries",
    label: "Administrative Boundaries",
    domain: "Demographic",
    loadGroup: "demographic",
    sourceType: "wfs",
    workspace: "panchayat_boundaries",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) => `${district}_${tehsil}`,
    styleProfile: "boundary",
  },
  {
    id: "demographics",
    label: "Socio-Economic Profile",
    domain: "Demographic",
    loadGroup: "demographic",
    sourceType: "wfs",
    workspace: "panchayat_boundaries",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) => `${district}_${tehsil}`,
    styleProfile: "demographics",
  },
  {
    id: "facilities",
    label: "Facilities Proximity",
    domain: "Village",
    loadGroup: "village-data",
    sourceType: "wfs",
    workspace: "facilities_proximity",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) => `facilities_${district}_${tehsil}`,
    styleProfile: "facilities",
  },
  {
    id: "antyodaya",
    label: "Mission Antyodaya (2020)",
    domain: "Village",
    loadGroup: "village-data",
    sourceType: "wfs",
    workspace: "antyodaya_2020",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) =>
      `antyodaya20_${district}_${tehsil}`,
    styleProfile: "antyodaya",
  },
  {
    id: "livestock",
    label: "Village Livestock Census",
    domain: "Village",
    loadGroup: "village-data",
    sourceType: "wfs",
    workspace: "livestocks",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) => `livestocks_${district}_${tehsil}`,
    styleProfile: "livestock",
  },
  {
    id: "hydrological_boundaries",
    label: "MicroWatershed Boundaries",
    domain: "Hydrology",
    loadGroup: "hydrology",
    sourceType: "wfs",
    workspace: "mws",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) => `mws_${district}_${tehsil}`,
    styleProfile: "boundary",
  },
  {
    id: "mws_layers",
    label: "Annual Water Balance",
    domain: "Hydrology",
    loadGroup: "hydrology",
    sourceType: "wfs",
    workspace: "mws_layers",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) =>
      `deltaG_well_depth_${district}_${tehsil}`,
    styleProfile: "mws",
  },
  {
    id: "mws_layers_fortnight",
    label: "Fortnightly Water Balance",
    domain: "Hydrology",
    loadGroup: "hydrology",
    sourceType: "wfs",
    workspace: "mws_layers",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) =>
      `deltaG_fortnight_${district}_${tehsil}`,
    styleProfile: "mws_fortnight",
  },
  {
    id: "terrain_vector",
    label: "Terrain Clusters",
    pairAfter: "terrain",
    domain: "Land",
    loadGroup: "land",
    sourceType: "wfs",
    workspace: "terrain",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) => `${district}_${tehsil}_cluster`,
    styleProfile: "terrain_vector",
  },
  {
    id: "soil_type",
    label: "Soil Type Vector",
    domain: "Land",
    loadGroup: "land",
    sourceType: "wfs",
    workspace: "soil_type",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) => `soil_type_${district}_${tehsil}`,
    styleProfile: "soil_type",
  },
  {
    id: "drainage",
    label: "Drainage Lines",
    domain: "Hydrology",
    loadGroup: "hydrology",
    sourceType: "wfs",
    workspace: "drainage",
    geometryType: "line",
    layerName: ({ district, tehsil }) => `${district}_${tehsil}`,
    styleProfile: "drainage",
  },
  {
    id: "river",
    label: "Rivers",
    domain: "Hydrology",
    loadGroup: "hydrology",
    sourceType: "wfs",
    workspace: "river",
    geometryType: "line",
    layerName: ({ district, tehsil }) =>
      `${district}_${tehsil}_river_vector`,
    styleProfile: "river",
  },
  {
    id: "canal",
    label: "Canals",
    domain: "Hydrology",
    loadGroup: "hydrology",
    sourceType: "wfs",
    workspace: "canal",
    geometryType: "line",
    layerName: ({ district, tehsil }) =>
      `${district}_${tehsil}_canal_vector`,
    styleProfile: "canal",
  },
  {
    id: "remote_sensed_waterbodies",
    label: "Surface Water Bodies",
    domain: "Hydrology",
    loadGroup: "hydrology",
    sourceType: "wfs",
    workspace: "swb",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) =>
      `surface_waterbodies_${district}_${tehsil}`,
    styleProfile: "waterbodies",
  },
  {
    id: "soge",
    label: "Stage of Groundwater Extraction",
    domain: "Hydrology",
    loadGroup: "hydrology",
    sourceType: "wfs",
    workspace: "soge",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) => `soge_vector_${district}_${tehsil}`,
    styleProfile: "soge",
  },
  {
    id: "aquifer",
    label: "Aquifer",
    domain: "Hydrology",
    loadGroup: "hydrology",
    sourceType: "wfs",
    workspace: "aquifer",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) =>
      `aquifer_vector_${district}_${tehsil}`,
    styleProfile: "aquifer",
  },
  {
    id: "cropping_intensity",
    label: "Cropping Intensity",
    domain: "Agriculture",
    loadGroup: "agriculture",
    sourceType: "wfs",
    workspace: "crop_intensity",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) => `${district}_${tehsil}_intensity`,
    styleProfile: "cropping_intensity",
  },
  {
    id: "drought",
    label: "Drought Severity",
    domain: "Agriculture",
    loadGroup: "agriculture",
    sourceType: "wfs",
    workspace: "drought",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) => `${district}_${tehsil}_drought`,
    styleProfile: "drought",
  },
  {
    id: "drought_causality",
    label: "Drought Causality",
    domain: "Agriculture",
    loadGroup: "agriculture",
    sourceType: "wfs",
    workspace: "drought_causality",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) => `${district}_${tehsil}_drought_causality`,
    styleProfile: "drought_causality",
  },
  ...GEOLIBRE_NREGA_CATEGORIES.map((category) => ({
    id: `nrega_${category.id}`,
    baseId: "nrega",
    label: category.label,
    legendTitle: "NREGA Assets",
    domain: "NREGA",
    loadGroup: "nrega",
    sourceType: "wfs",
    workspace: "nrega_assets",
    geometryType: "point",
    layerName: ({ district, tehsil }) => `${district}_${tehsil}`,
    styleProfile: "nrega",
    nregaCategoryId: category.id,
  })),
  {
    id: "green_credit",
    label: "Green Credit Projects",
    domain: "Restoration",
    loadGroup: "restoration",
    sourceType: "wfs",
    workspace: "green_credit",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) =>
      `${district}_${tehsil}_green_credit`,
    styleProfile: "green_credit",
  },
  {
    id: "tree_in_grassland",
    label: "Tree in Grassland",
    domain: "Restoration",
    loadGroup: "restoration",
    sourceType: "wfs",
    workspace: "tree_in_grassland",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) => `${district}_${tehsil}_tree_in_grassland`,
    styleProfile: "tree_in_grassland",
  },
  {
    // Same physical tree_in_grassland source as "tree_in_grassland" above,
    // styled on a different property. Data identity is shared; only the
    // presentation differs.
    id: "tree_in_grassland_loss",
    label: "Tree in Grassland: Loss",
    domain: "Trees",
    loadGroup: "trees",
    sourceType: "wfs",
    workspace: "tree_in_grassland",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) => `${district}_${tehsil}_tree_in_grassland`,
    styleProfile: "tree_in_grassland_loss",
  },
  {
    id: "forest_fringe",
    label: "Forest Fringe",
    domain: "Restoration",
    loadGroup: "restoration",
    sourceType: "wfs",
    workspace: "forest_fringes",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) => `${district}_${tehsil}_forest_fringe`,
    styleProfile: "forest_fringe",
  },
  {
    // Same physical forest_fringes source as "forest_fringe" above, styled
    // on a different property. Data identity is shared; only the presentation differs.
    id: "forest_fringe_deforestation",
    label: "Forest Fringe: Deforestation",
    domain: "Trees",
    loadGroup: "trees",
    sourceType: "wfs",
    workspace: "forest_fringes",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) => `${district}_${tehsil}_forest_fringe`,
    styleProfile: "forest_fringe_deforestation",
  },
  {
    id: "forest_fire",
    label: "Forest Fire",
    domain: "Trees",
    loadGroup: "trees",
    sourceType: "wfs",
    workspace: "forest_fire",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) => `${district}_${tehsil}_forest_fire`,
    styleProfile: "forest_fire",
  },
  {
    id: "land_conflicts",
    label: "Land Conflicts",
    domain: "Industry",
    loadGroup: "industry",
    sourceType: "wfs",
    workspace: "lcw",
    geometryType: "point",
    layerName: ({ district, tehsil }) =>
      `${district}_${tehsil}_lcw_conflict`,
    styleProfile: "industry_point",
  },
  {
    id: "industry",
    label: "Industries and CSR",
    domain: "Industry",
    loadGroup: "industry",
    sourceType: "wfs",
    workspace: "factory_csr",
    geometryType: "point",
    layerName: ({ district, tehsil }) =>
      `${district}_${tehsil}_factory_csr`,
    styleProfile: "industry_point",
  },
  {
    id: "mining",
    label: "Mining Sites",
    domain: "Industry",
    loadGroup: "industry",
    sourceType: "wfs",
    workspace: "mining",
    geometryType: "point",
    layerName: ({ district, tehsil }) => `${district}_${tehsil}_mining`,
    styleProfile: "industry_point",
  },
  {
    id: "terrain",
    label: "Terrain",
    domain: "Land",
    loadGroup: "land",
    sourceType: "wms",
    workspace: "terrain",
    layerName: ({ district, tehsil }) => `${district}_${tehsil}_terrain_raster`,
    // An empty rasterStyle uses this GeoServer layer's published default style.
    // Named styles are passed to WMS as STYLES for every display request.
    rasterStyle: "Terrain_Style_11_Classes",
    defaultVisible: true,
  },
  {
    id: "soil_health_vector",
    label: "Soil Health",
    pairAfter: "soil_health_raster_OC_OLM",
    domain: "Land",
    loadGroup: "land",
    sourceType: "wfs",
    workspace: "soil_health_vector",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) =>
      `${district}_${tehsil}_soil_health_vector`,
    styleProfile: "boundary",
  },
  {
    id: "dem",
    label: "Digital Elevation Model",
    domain: "Land",
    loadGroup: "land",
    sourceType: "wms",
    workspace: "dem",
    layerName: ({ district, tehsil }) => `${district}_${tehsil}_dem_raster`,
    rasterStyle: "dem_grayscale",
  },
  {
    id: "soil_health_raster_n",
    label: "Soil Nitrogen Levels",
    domain: "Land",
    loadGroup: "land",
    sourceType: "wms",
    workspace: "soil_health_raster",
    layerName: ({ district, tehsil }) =>
      `${district}_${tehsil}_soil_health_raster_N`,
    rasterStyle: "Soil_Health_Nitrogen",
    defaultVisible: false,
  },
  {
    id: "soil_health_raster_P",
    label: "Soil Phosphorus Levels",
    domain: "Land",
    loadGroup: "land",
    sourceType: "wms",
    workspace: "soil_health_raster",
    layerName: ({ district, tehsil }) =>
      `${district}_${tehsil}_soil_health_raster_P`,
    rasterStyle: "Soil_Health_Phosphorus",
    defaultVisible: false,
  },
  {
    id: "soil_health_raster_K",
    label: "Soil Potassium Levels",
    domain: "Land",
    loadGroup: "land",
    sourceType: "wms",
    workspace: "soil_health_raster",
    layerName: ({ district, tehsil }) =>
      `${district}_${tehsil}_soil_health_raster_K`,
    rasterStyle: "Soil_Health_Potassium",
    defaultVisible: false,
  },
  {
    id: "soil_health_raster_OC",
    label: "Soil Organic Carbon Concentration",
    domain: "Land",
    loadGroup: "land",
    sourceType: "wms",
    workspace: "soil_health_raster",
    layerName: ({ district, tehsil }) =>
      `${district}_${tehsil}_soil_health_raster_OC`,
    rasterStyle: "Soil_Health_Organic_carbon",
    defaultVisible: false,
  },
  {
    id: "soil_health_raster_OC_OLM",
    label: "Soil Organic Carbon Concentration for Various Forest Systems",
    domain: "Land",
    loadGroup: "land",
    sourceType: "wms",
    workspace: "soil_health_raster",
    layerName: ({ district, tehsil }) =>
      `${district}_${tehsil}_soil_health_raster_OC_OLM`,
    rasterStyle: "Soil_Health_OC_OLM",
    defaultVisible: false,
  },
  {
    id: "clart",
    label: "CLART",
    domain: "Hydrology",
    loadGroup: "hydrology",
    sourceType: "wms",
    workspace: "clart",
    layerName: ({ district, tehsil }) => `${district}_${tehsil}_clart`,
    rasterStyle: "",
  },
  ...[
    { id: "distance_to_drainage_line", label: "Distance to Drainage Line", workspace: "distance_nearest_upstream_DL", style: "distance_nearest_upstream_DL", name: ({ district, tehsil }) => `distance_to_drainage_line_${district}_${tehsil}_raster` },
    { id: "catchment_area", label: "Catchment Area", workspace: "catchment_area_singleflow", style: "catchment_area_singleflow", name: ({ district, tehsil }) => `catchment_area_${district}_${tehsil}_raster` },
    { id: "natural_depression", label: "Natural Depression", workspace: "natural_depression", style: "natural_depression", name: ({ district, tehsil }) => `natural_depression_${district}_${tehsil}_raster` },
  ].map((entry) => ({
    id: entry.id, label: entry.label, domain: "Hydrology", loadGroup: "hydrology",
    sourceType: "wms", workspace: entry.workspace, layerName: entry.name,
    rasterStyle: entry.style,
  })),
  // Both workspaces publish 2017–2023. Years are separate selectable rasters.
  ...[
    { id: "tree_canopy_density", label: "Canopy Cover Density", workspace: "tree_ccd_raster", style: "tree_ccd_style", prefix: "ccd" },
    { id: "tree_height", label: "Tree Height", workspace: "tree_ch_raster", style: "tree_ch_style", prefix: "ch" },
  ].flatMap(entry => Array.from({ length: 7 }, (_, i) => {
    const year = 2023 - i;
    return {
      id: `${entry.id}_${year}`, baseId: entry.id, label: `${entry.label} (${year})`,
      domain: "Trees", loadGroup: "trees", sourceType: "wms",
      workspace: entry.workspace, rasterStyle: entry.style, year: String(year),
      layerName: ({ district, tehsil }) => `${entry.prefix}_raster_${district}_${tehsil}_${year}`,
    };
  })),
  {
    id: "forest_change", label: "Forest Change", domain: "Trees", loadGroup: "trees",
    sourceType: "wms", workspace: "tree_overall_raster", rasterStyle: "tree_overall_style",
    layerName: ({ district, tehsil }) => `overall_change_raster_${district}_${tehsil}`,
  },
  {
    id: "afforestation",
    label: "Change Detection: Afforestation",
    domain: "Restoration",
    loadGroup: "restoration",
    sourceType: "wms",
    workspace: "change_detection",
    layerName: ({ district, tehsil }) =>
      `change_${district}_${tehsil}_Afforestation`,
    rasterStyle: "",
  },
  {
    id: "afforestation_stats",
    label: "Change Detection: Afforestation",
    pairAfter: "afforestation",
    domain: "Restoration",
    loadGroup: "restoration",
    sourceType: "wfs",
    workspace: "change_detection",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) =>
      `change_vector_${district}_${tehsil}_Afforestation`,
    styleProfile: "afforestation_stats",
  },
  {
    id: "deforestation",
    label: "Change Detection: Deforestation",
    domain: "Restoration",
    loadGroup: "restoration",
    sourceType: "wms",
    workspace: "change_detection",
    layerName: ({ district, tehsil }) =>
      `change_${district}_${tehsil}_Deforestation`,
    rasterStyle: "",
  },
  {
    id: "deforestation_stats",
    label: "Change Detection: Deforestation",
    pairAfter: "deforestation",
    domain: "Restoration",
    loadGroup: "restoration",
    sourceType: "wfs",
    workspace: "change_detection",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) =>
      `change_vector_${district}_${tehsil}_Deforestation`,
    styleProfile: "deforestation_stats",
  },
  {
    id: "degradation",
    label: "Change Detection: Degradation",
    domain: "Restoration",
    loadGroup: "restoration",
    sourceType: "wms",
    workspace: "change_detection",
    layerName: ({ district, tehsil }) =>
      `change_${district}_${tehsil}_Degradation`,
    rasterStyle: "",
  },
  {
    id: "degradation_stats",
    label: "Change Detection: Degradation",
    pairAfter: "degradation",
    domain: "Restoration",
    loadGroup: "restoration",
    sourceType: "wfs",
    workspace: "change_detection",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) =>
      `change_vector_${district}_${tehsil}_Degradation`,
    styleProfile: "degradation_stats",
  },
  {
    id: "urbanization",
    label: "Change Detection: Urbanization",
    domain: "Restoration",
    loadGroup: "restoration",
    sourceType: "wms",
    workspace: "change_detection",
    layerName: ({ district, tehsil }) =>
      `change_${district}_${tehsil}_Urbanization`,
    rasterStyle: "",
  },
  {
    id: "urbanization_stats",
    label: "Change Detection: Urbanization",
    pairAfter: "urbanization",
    domain: "Restoration",
    loadGroup: "restoration",
    sourceType: "wfs",
    workspace: "change_detection",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) =>
      `change_vector_${district}_${tehsil}_Urbanization`,
    styleProfile: "urbanization_stats",
  },
  {
    id: "cropintensity",
    label: "Change Detection: Crop Intensity",
    domain: "Restoration",
    loadGroup: "restoration",
    sourceType: "wms",
    workspace: "change_detection",
    layerName: ({ district, tehsil }) =>
      `change_${district}_${tehsil}_CropIntensity`,
    rasterStyle: "cropintensity",
  },
  {
    id: "cropintensity_stats",
    label: "Change Detection: Crop Intensity",
    pairAfter: "cropintensity",
    domain: "Restoration",
    loadGroup: "restoration",
    sourceType: "wfs",
    workspace: "change_detection",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) =>
      `change_vector_${district}_${tehsil}_CropIntensity`,
    styleProfile: "cropintensity_stats",
  },
  {
    id: "shrubland_diversion_stats",
    label: "Change: Shrubland Diversion",
    domain: "Restoration",
    loadGroup: "restoration",
    sourceType: "wfs",
    workspace: "change_detection",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) =>
      `change_vector_${district}_${tehsil}_ShrubChange`,
    styleProfile: "shrubland_diversion_stats",
  },
  {
    id: "shrubland_diversion_base",
    label: "Change: Shrubland Diversion",
    domain: "Restoration",
    loadGroup: "restoration",
    sourceType: "wms",
    workspace: "change_detection",
    layerName: ({ district, tehsil }) => `change_${district}_${tehsil}_ShrubChange`,
    rasterStyle: "change_shrubland_diversion_style",
    stylePublicationPending: true,
  },
  {
    id: "restoration",
    label: "Restoration Opportunities",
    domain: "Restoration",
    loadGroup: "restoration",
    sourceType: "wms",
    workspace: "restoration",
    layerName: ({ district, tehsil }) =>
      `restoration_${district}_${tehsil}_raster`,
    rasterStyle: "restoration_style",
  },
  {
    id: "restoration_stats",
    label: "Restoration Opportunities",
    pairAfter: "restoration",
    domain: "Restoration",
    loadGroup: "restoration",
    sourceType: "wfs",
    workspace: "restoration",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) =>
      `restoration_${district}_${tehsil}_vector`,
    styleProfile: "restoration_stats",
  },
  {
    id: "lulc_stats",
    label: "LULC",
    pairAfter: `lulc_level_3_${LATEST_GEOLIBRE_LULC_YEAR}`,
    domain: "Land",
    loadGroup: "lulc",
    sourceType: "wfs",
    workspace: "lulc_vector",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) => `lulc_vector_${district}_${tehsil}`,
    styleProfile: "lulc_stats",
  },
  ...[
    { id: "ndvi_crop_stats", label: "NDVI · Cropland", suffix: "_crop" },
    { id: "ndvi_shrub_stats", label: "NDVI · Shrubland", suffix: "_shrub" },
    { id: "ndvi_tree_stats", label: "NDVI · Tree Cover", suffix: "_tree" },
  ].map((entry) => ({
    id: entry.id,
    label: entry.label,
    domain: "Land",
    loadGroup: "lulc",
    sourceType: "wfs",
    workspace: "ndvi_timeseries",
    geometryType: "polygon",
    layerName: ({ district, tehsil }) => `ndvi_timeseries_${district}_${tehsil}${entry.suffix}`,
    styleProfile: "ndvi",
  })),
];

export const GEOLIBRE_LULC_LAYERS = GEOLIBRE_LULC_YEARS.map((year) => ({
  id: `lulc_level_3_${year.value}`,
  baseId: "lulc_level_3",
  label: `LULC · ${year.label}`,
  domain: "Land",
  workspace: LULC_SOURCE_WORKSPACE,
  rasterStyle: "lulc_land_use_KYL",
  loadGroup: "lulc",
  sourceType: "wms",
  useGlobalWms: true,
  year: year.value,
  layerName: ({ district, tehsil }) =>
    `LULC_${year.value}_${district}_${tehsil}_level_3`,
}));

const UNIT_SOURCES_BY_ID = {
  administrative_boundaries: ["admin_boundary"],
  demographics: ["admin_boundary"],
  facilities: ["facilities_proximity"],
  antyodaya: ["cs_antyodaya_2020"],
  livestock: ["cs_village_livestock_census_20"],
  hydrological_boundaries: ["mws"],
  mws_layers: ["well_depth_net_value", "filtered_delta_g_annual_uid"],
  mws_layers_fortnight: ["filtered_delta_g_fortnight_uid"],
  terrain_vector: ["terrain_clusters"],
  soil_type: ["soil_type"],
  drainage: ["drainage_lines"],
  river: ["river_vector"],
  canal: ["canal_vector"],
  remote_sensed_waterbodies: ["swb3"],
  soge: ["soge_vector"],
  aquifer: ["aquifer_vector"],
  cropping_intensity: ["cropping_intensity_2017-23"],
  drought: ["drought_2017_2022"],
  green_credit: ["green_credits_vector"],
  land_conflicts: ["land_conflict_watch_vector"],
  industry: ["factory_csr_vector"],
  mining: ["mining_vector"],
  soil_health_vector: ["soil_health_vector"],
  afforestation_stats: ["change_vector_Afforestation"],
  deforestation_stats: ["change_vector_Deforestation"],
  degradation_stats: ["change_vector_Degradation"],
  urbanization_stats: ["change_vector_Urbanization"],
  cropintensity_stats: ["change_vector_CropIntensity"],
  shrubland_diversion_stats: ["change_vector_ShrubChange"],
  restoration_stats: ["restoration_vector"],
  lulc_stats: ["lulc_vector"],
  ndvi_crop_stats: ["ndvi_timeseries"],
  ndvi_shrub_stats: ["ndvi_timeseries"],
  ndvi_tree_stats: ["ndvi_timeseries"],
  drought_causality: ["drought_causality"],
  tree_in_grassland: ["tree_in_grassland"],
  tree_in_grassland_loss: ["tree_in_grassland"],
  forest_fringe: ["forest_fringe"],
  forest_fringe_deforestation: ["forest_fringe"],
  forest_fire: ["forest_fire"],
};

const PRESENTATION_BY_ID = new Map(PRESENTATION.map((entry, index) => [entry.id, { ...entry, index }]));
const catalog = [
  ...LAYERS,
  ...GEOLIBRE_LULC_LAYERS,
];
if (PRESENTATION_BY_ID.size !== PRESENTATION.length ||
    catalog.length !== PRESENTATION.length ||
    catalog.some((layer) => !PRESENTATION_BY_ID.has(layer.id))) {
  throw new Error("GeoLibre catalog and finalized layer presentation are out of sync.");
}
for (const layer of catalog) {
  const presentation = PRESENTATION_BY_ID.get(layer.id);
  const sourcePattern = `${layer.workspace}:${layer.layerName({ district: "{district}", tehsil: "{tehsil}" })}`;
  if (presentation.sourceType !== layer.sourceType ||
      presentation.workspace !== layer.workspace || presentation.sourcePattern !== sourcePattern) {
    throw new Error(`GeoLibre source and CSV disagree for ${layer.id}.`);
  }
}
export const GEOLIBRE_LAYERS = Object.freeze(catalog.map((layer) => ({
  ...layer,
  label: PRESENTATION_BY_ID.get(layer.id).label,
  category: PRESENTATION_BY_ID.get(layer.id).category,
  loadGroup: PRESENTATION_BY_ID.get(layer.id).groupId,
  groupName: PRESENTATION_BY_ID.get(layer.id).groupName,
  domain: PRESENTATION_BY_ID.get(layer.id).groupName,
  defaultVisible: PRESENTATION_BY_ID.get(layer.id).defaultVisible,
  defaultProperty: PRESENTATION_BY_ID.get(layer.id).defaultProperty,
  tooltip: PRESENTATION_BY_ID.get(layer.id).tooltip || null,
  description: PRESENTATION_BY_ID.get(layer.id).description
    || CATALOG.layerDescriptionsBySource[PRESENTATION_BY_ID.get(layer.id).descriptionKey]
    || null,
  ...(layer.sourceType === "wms"
    ? { rasterStyle: PRESENTATION_BY_ID.get(layer.id).style }
    : { styleProfile: PRESENTATION_BY_ID.get(layer.id).style }),
  ...(layer.sourceType === "wfs" ? {
    unitSources: UNIT_SOURCES_BY_ID[layer.id] || (layer.baseId === "nrega" ? ["nrega_assets"] : []),
  } : {}),
})).sort((a, b) => PRESENTATION_BY_ID.get(a.id).index - PRESENTATION_BY_ID.get(b.id).index));

export const GEOLIBRE_VECTOR_LAYERS = GEOLIBRE_LAYERS.filter(
  (layer) => layer.sourceType === "wfs"
);

export const GEOLIBRE_RASTER_LAYERS = GEOLIBRE_LAYERS.filter(
  (layer) => layer.sourceType === "wms"
);
